/**
 * Turning an unknown caught value into a real `Error` — without losing the
 * message.
 *
 * `@supabase/postgrest-js` builds a `PostgrestError extends Error` only under
 * `shouldThrowOnError()`. Nothing in this repo calls `.throwOnError()`, so
 * every `{ data, error }` a query resolves to carries the *parsed JSON body*
 * as a plain object — `{ message, details, hint, code }` — with no prototype
 * chain to `Error`. A hook that does `if (error) throw error` and then
 * `catch (err) { err instanceof Error ? err : new Error(String(err)) }`
 * therefore shows the user the literal string `"[object Object]"` instead of
 * the reason the query failed. That is the defect this module exists to make
 * unrepresentable; see `docs/FAILURE_PATTERNS.md` #9.
 *
 * Platform-agnostic on purpose (no DOM, no React Native) — `packages/*` is
 * shared by both app targets.
 */

/**
 * The shape PostgREST answers with. Every field but `message` can be absent
 * or null depending on which layer produced the failure (RLS denial, a
 * constraint violation, and a bad request all fill in different subsets).
 */
export interface PostgrestErrorShape {
  message: string;
  details?: string | null;
  hint?: string | null;
  code?: string | null;
}

/**
 * An `Error` that also carries whatever PostgREST diagnostics came with it.
 *
 * The message is what a toast or an error region shows; `code` is what a
 * caller branches on (`'23505'` for a unique violation, `'42501'` for an RLS
 * denial, `'PGRST116'` for "no rows"), and it is only reachable if the
 * normalisation keeps it.
 */
export class QueryError extends Error {
  readonly details: string | null;
  readonly hint: string | null;
  readonly code: string | null;

  constructor(message: string, fields: { details?: unknown; hint?: unknown; code?: unknown } = {}) {
    super(message);
    this.name = 'QueryError';
    this.details = asText(readProp(fields, 'details'));
    this.hint = asText(readProp(fields, 'hint'));
    this.code = asText(readProp(fields, 'code'));
  }
}

const FALLBACK_MESSAGE = 'Unexpected error';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A non-empty string for anything that can honestly be rendered as one. */
function asText(value: unknown): string | null {
  if (typeof value === 'string') return value.trim() === '' ? null : value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
}

/**
 * Read one property without trusting the object to be inert data.
 *
 * Everything this normalises today is `JSON.parse` output — plain data, never
 * accessors — so a throwing getter is not reachable from PostgREST. It is
 * guarded anyway because this runs on every error path in both apps: a throw
 * from inside the error handler destroys the report of the original failure
 * and replaces it with an unrelated one, which is strictly worse than the
 * "[object Object]" this file exists to eliminate.
 */
function readProp(source: Record<string, unknown>, key: string): unknown {
  try {
    return source[key];
  } catch {
    return undefined;
  }
}

/**
 * Normalise a caught or returned failure into an `Error`.
 *
 * - a real `Error` (including `PostgrestError`, `AuthError`, `FunctionsError`)
 *   is returned untouched, so nothing that was already right is re-wrapped;
 * - a PostgREST-shaped plain object becomes a {@link QueryError} carrying its
 *   real `message` plus `details`/`hint`/`code`;
 * - a string becomes its own message;
 * - `null`, `undefined`, and objects with no usable `message` become
 *   `fallbackMessage` — never `"[object Object]"`.
 */
export function toError(value: unknown, fallbackMessage: string = FALLBACK_MESSAGE): Error {
  if (value instanceof Error) return value;
  if (typeof value === 'string') return new Error(asText(value) ?? fallbackMessage);
  if (isRecord(value)) {
    return new QueryError(asText(readProp(value, 'message')) ?? fallbackMessage, value);
  }
  return new Error(fallbackMessage);
}

/** The message {@link toError} would produce, for callers that only want text. */
export function toErrorMessage(value: unknown, fallbackMessage: string = FALLBACK_MESSAGE): string {
  return toError(value, fallbackMessage).message;
}
